import { useMemo } from 'react';
import { CallRouteType } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { PhoneLocationFilterStore, SettingsTenantLocation } from '@frontend/phone-config';
import { TableRowActions } from '@frontend/design-system';
import { CallRoutesListTable } from '../call-routes-list-table';
import { filterCallRoutesOnly } from '../helpers/call-routes';
import { NumbersPanelTabs } from '../numbers-panel/call-route-tabs';
import { ExtendedCallRoute } from '../types';
import { RoutesTableTab, RoutesTableTabType } from './routes-tabs';

type RoutesListTableProps = {
  tableData: ExtendedCallRoute[];
  isLoading: boolean;
  settingsTenantLocation: SettingsTenantLocation;
  tableRowActions?: {
    callRouteRowActions?: TableRowActions<ExtendedCallRoute>;
    deviceRouteRowActions?: TableRowActions<ExtendedCallRoute>;
  };
  clickableCallRoutes?: boolean;
  initialSelectedCallRouteId?: string;
  unsavedChangesCallRouteIds?: string[];
  onMoreTextClick?: (tab: NumbersPanelTabs, callRoute?: ExtendedCallRoute) => void;
  onRowSelect?: (callRoute: ExtendedCallRoute) => void;
  filteredLocations: string[];
  fullHeight?: boolean;
  setLocationFilters: PhoneLocationFilterStore['setLocationFilters'];
  selectedTab?: RoutesTableTabType;
  rightToolbarContent?: React.ReactNode;
};

export const RoutesListTable = ({
  tableData,
  isLoading,
  settingsTenantLocation,
  unsavedChangesCallRouteIds,
  filteredLocations,
  tableRowActions,
  selectedTab = 'call-routes',
  clickableCallRoutes = true,
  initialSelectedCallRouteId,
  fullHeight = true,
  rightToolbarContent,
  onRowSelect,
  setLocationFilters,
  onMoreTextClick,
}: RoutesListTableProps) => {
  const { callRoutes, deviceRoutes } = useMemo(() => {
    // Group the routes by type: call routes vs device routes
    const callRoutesOnly = filterCallRoutesOnly(tableData);
    const deviceRoutesOnly = tableData.filter(
      (route) => route.callRouteType === CallRouteType.CALL_ROUTE_TYPE_EXTENSION
    );
    return { callRoutes: callRoutesOnly, deviceRoutes: deviceRoutesOnly };
  }, [tableData]);

  return (
    <>
      {selectedTab === RoutesTableTab.CALL_ROUTES && (
        <CallRoutesListTable
          tableData={callRoutes}
          filteredLocations={filteredLocations}
          setLocationFilters={setLocationFilters}
          isLoading={isLoading}
          settingsTenantLocation={settingsTenantLocation}
          unsavedChangesCallRouteIds={unsavedChangesCallRouteIds}
          onMoreTextClick={onMoreTextClick}
          tableRowActions={tableRowActions?.callRouteRowActions}
          clickableCallRoutes={clickableCallRoutes}
          onRowSelect={onRowSelect}
          initialSelectedCallRouteId={initialSelectedCallRouteId}
          fullHeight={fullHeight}
          rightToolbarContent={rightToolbarContent}
        />
      )}

      {selectedTab === RoutesTableTab.DEVICE_ROUTES && (
        <CallRoutesListTable
          tableData={deviceRoutes}
          filteredLocations={filteredLocations}
          setLocationFilters={setLocationFilters}
          isLoading={isLoading}
          settingsTenantLocation={settingsTenantLocation}
          unsavedChangesCallRouteIds={unsavedChangesCallRouteIds}
          onMoreTextClick={onMoreTextClick}
          tableRowActions={tableRowActions?.deviceRouteRowActions}
          clickableCallRoutes={clickableCallRoutes}
          onRowSelect={onRowSelect}
          initialSelectedCallRouteId={initialSelectedCallRouteId}
          fullHeight={fullHeight}
          routeType='device-extension-routes'
          rightToolbarContent={rightToolbarContent}
        />
      )}
    </>
  );
};
